import React from 'react';
import TwDialog from '../TwDialog/TwDialog';
import { SwipeableDrawer } from '@mui/material';
import {
  usePatchTripDriverAckByTokenMutation,
  usePatchTripDriverAckMutation
} from '../../redux/api/trips/tripApi';
import { TripDriverAckStatus } from '../../redux/api/trips/trips.model';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { useAppDispatch } from '../../redux/hooks';
import { setLoadDetailReload } from '../../redux/features/navigation/navigationSlice';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import {
  StyledBoxDriverAckConfirmationModal,
  StyledPullerDriverAckConfirmationModal
} from './TwDriverAckConfirmationModal.styles';
import { TwDriverAckConfirmationModalContent } from './TwDriverAckConfirmationModalContent';

interface TwDriverAckConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  driverId: string;
  tripId?: string;
  orderId?: string;
  token?: string;
}

export const TwDriverAckConfirmationModal: React.FC<TwDriverAckConfirmationModalProps> = ({
  open,
  onClose,
  driverId,
  tripId,
  orderId,
  token
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [updateTripDriverAckByToken, { isLoading }] = usePatchTripDriverAckByTokenMutation();
  const [updateTripDriverAck, { isLoading: isLoadingTrip }] = usePatchTripDriverAckMutation();

  const handleConfirm = (): void => {
    if (token) updateTripDriverAckOnDriverLink();
    if (tripId && !token) updateTripDriverAckOnTrip();
  };

  const updateTripDriverAckOnDriverLink = (): void => {
    if (!token) return;

    updateTripDriverAckByToken({
      token,
      tripId: tripId as string,
      body: { driver_ack_status: TripDriverAckStatus.CONFIRMED, driver_id: driverId }
    })
      .then(() => {
        addNotification({
          message: 'Trip confirmed successfully',
          variant: 'success'
        });

        onClose();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        addNotification({
          message: 'Something went wrong while confirming the trip',
          variant: 'error'
        });
      });
  };

  const updateTripDriverAckOnTrip = (): void => {
    if (!tripId) return;

    updateTripDriverAck({
      tripId,
      body: { driver_ack_status: TripDriverAckStatus.CONFIRMED, driver_id: driverId }
    })
      .then(() => {
        addNotification({
          message: 'Trip confirmed successfully',
          variant: 'success'
        });

        if (orderId) dispatch(setLoadDetailReload(true));
        onClose();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        addNotification({
          message: 'Something went wrong while confirming the trip',
          variant: 'error'
        });
      });
  };

  return (
    <>
      {!matchesMobileOrTablet ? (
        <TwDialog
          title=""
          hideAppBar
          open={open}
          onClickClose={onClose}
          size="xs"
          disableEscapeKeyDown={true}
        >
          <TwDriverAckConfirmationModalContent
            handleConfirm={handleConfirm}
            isLoading={isLoading}
            isLoadingTrip={isLoadingTrip}
          />
        </TwDialog>
      ) : (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          disableDiscovery
          disableSwipeToOpen
          swipeAreaWidth={0}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClose={(event: any): void => {
            if (event.type === 'keydown' && event.key === 'Escape') return;
            if (event.type === 'click') return;
            onClose();
          }}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onOpen={(): void => {}}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              pointerEvents: 'auto', // ensure content is interactive
              touchAction: 'none' // disable swipe gestures
            }
          }}
        >
          <StyledBoxDriverAckConfirmationModal
            sx={{
              paddingTop: '56px',
              paddingBottom: '22px',
              paddingLeft: '11px',
              paddingRight: '11px',
              height: '100%',
              overflow: 'auto'
            }}
            onClick={(e): void => e.stopPropagation()}
            onTouchStart={(e): void => e.stopPropagation()}
            onTouchMove={(e): void => e.stopPropagation()}
            onTouchEnd={(e): void => e.stopPropagation()}
          >
            <StyledPullerDriverAckConfirmationModal />

            <TwDriverAckConfirmationModalContent
              handleConfirm={handleConfirm}
              isLoading={isLoading}
              isLoadingTrip={isLoadingTrip}
            />
          </StyledBoxDriverAckConfirmationModal>
        </SwipeableDrawer>
      )}
    </>
  );
};
